// @section 1 Command / Clients — the clients Adam is actively working on + the work due.
// Curated cockpit view (the deep work lives in the Clients/CRM/PM modules; this is the
// front-page roll-up). Data model from the 2026-06-19 delivery-data map:
//   - hq_delivery.* is EMPTY, so the client spine is hq_crm.deals (stage won/proposal)
//     joined to hq_crm.companies.
//   - the real "work due" lives in hq_pm.tasks: a few are FK-linked (parent_type deal/company),
//     most are standalone tasks named-by-title, so we bridge with a name match and surface an
//     "unlinked work" gap tile so the dirtiness is visible, not hidden.
// All client-side off existing endpoints (listDeals / listCompanies / listTasks) — no new API.
// Spec: _docs/optiq-hq/command-today-spec-2026-06-19.md (delivery section).

import { useEffect, useMemo, useState } from 'react';
import { listDeals, listCompanies, listTasks, type Deal, type Company, type Task } from '../../api';

// @subsection 1.1 Helpers
const CLIENT_STAGES = new Set(['won', 'proposal']);
const OPEN_PARENTS = new Set(['standalone', 'cal_event', 'email_thread']);

function todayStr(): string {
  // Local calendar day as yyyy-mm-dd (no Date.now ban in the SPA runtime).
  return new Date().toISOString().slice(0, 10);
}
function fmtDue(d?: string): string {
  if (!d) return '';
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
// First significant token of a name, lowercased + de-punctuated. Used to bridge
// standalone task titles ("Wales: ...", "SawaOS: ...") back to a client.
function matchToken(name: string): string {
  const w = (name || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').trim().split(/\s+/)[0] ?? '';
  return w;
}

type WorkItem = { id: string; title: string; due?: string; overdue: boolean; linked: boolean };
type ClientCard = {
  name: string;
  stage: string;
  valueCents?: number;
  work: WorkItem[];
  nextDue?: string;
  hasOverdue: boolean;
  unlinkedCount: number;
};

// @subsection 1.2 Assemble client cards from deals + companies + tasks
function buildCards(deals: Deal[], companies: Company[], tasks: Task[]): ClientCard[] {
  const today = todayStr();
  const companyName = new Map(companies.map((c) => [c.id, c.name]));
  // Roster = Adam-curated active clients (hq_crm.companies.is_active_client), grouped by
  // their deal stage. Keeps defunct won deals (VPG/DomusOS) + stale proposals off the page
  // without rewriting any deal's sales history.
  const activeCompany = new Set(companies.filter((c) => c.isActiveClient).map((c) => c.id));
  const clientDeals = deals.filter(
    (d) => CLIENT_STAGES.has(d.stage) && !!d.companyId && activeCompany.has(d.companyId),
  );

  const cards: ClientCard[] = clientDeals.map((d) => {
    const cName = (d.companyId && companyName.get(d.companyId)) || d.name;
    const token = matchToken(cName);
    const seen = new Set<string>();
    const work: WorkItem[] = [];

    for (const t of tasks) {
      if (seen.has(t.id)) continue;
      const fkLinked =
        (t.parentType === 'deal' && t.parentId === d.id) ||
        (t.parentType === 'company' && !!d.companyId && t.parentId === d.companyId);
      const nameMatched =
        OPEN_PARENTS.has(t.parentType) && token.length >= 3 &&
        (t.title || '').toLowerCase().includes(token);
      if (!fkLinked && !nameMatched) continue;
      seen.add(t.id);
      work.push({
        id: t.id,
        title: t.title,
        due: t.dueDate,
        overdue: !!t.dueDate && t.dueDate < today,
        linked: fkLinked,
      });
    }

    work.sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999'));
    return {
      name: cName,
      stage: d.stage,
      valueCents: d.valueCents,
      work,
      nextDue: work.find((w) => w.due)?.due,
      hasOverdue: work.some((w) => w.overdue),
      unlinkedCount: work.filter((w) => !w.linked).length,
    };
  });

  // Sort: overdue first, then in-flight (proposal) before won, then by earliest next-due.
  const stageRank = (s: string) => (s === 'proposal' ? 0 : 1);
  cards.sort((a, b) =>
    Number(b.hasOverdue) - Number(a.hasOverdue) ||
    stageRank(a.stage) - stageRank(b.stage) ||
    (a.nextDue ?? '9999').localeCompare(b.nextDue ?? '9999'),
  );
  return cards;
}

const STAGE_CLASS: Record<string, string> = {
  won: 'border-ok/40 text-ok',
  proposal: 'border-accent/50 text-accent',
};

// @section 2 Page component
export default function Clients() {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [error, setError] = useState<string | null>(null);

  // @block 2.1 Load deals + companies + open tasks
  useEffect(() => {
    Promise.all([listDeals(), listCompanies(), listTasks({ status: 'todo' })])
      .then(([d, c, t]) => { setDeals(d); setCompanies(c); setTasks(t); setError(null); })
      .catch((e) => setError(String(e)));
  }, []);

  const cards = useMemo(
    () => (deals ? buildCards(deals, companies, tasks) : []),
    [deals, companies, tasks],
  );
  const totalUnlinked = useMemo(() => cards.reduce((a, c) => a + c.unlinkedCount, 0), [cards]);

  if (error) return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-sm">{error}</div>;
  if (!deals) return <div className="text-muted text-sm">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto">
      {/* @block 2.2 Masthead */}
      <div className="border-b-2 border-paper-100/20 pb-3 mb-6">
        <div className="text-[11px] uppercase tracking-[3px] text-muted">Active clients · work due</div>
        <h1 className="text-3xl md:text-4xl font-bold text-paper-100 tracking-tight">Clients</h1>
      </div>

      {/* @block 2.3 Client cards */}
      {cards.length === 0 ? (
        <div className="text-muted text-sm mb-8">No active clients (won or in-flight deals) found.</div>
      ) : (
        <div className="space-y-3 mb-8">
          {cards.map((c) => (
            <div key={c.name} className="rounded-xl border border-paper-100/10 bg-ink-900 p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-base font-semibold text-paper-100 truncate">{c.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-[1px] ${STAGE_CLASS[c.stage] ?? 'border-paper-100/20 text-muted'}`}>
                  {c.stage === 'proposal' ? 'in-flight' : c.stage}
                </span>
                {c.valueCents ? <span className="text-[11px] text-muted">${Math.round(c.valueCents / 100).toLocaleString()}</span> : null}
                {c.hasOverdue && <span className="text-[10px] text-stall ml-auto font-semibold">overdue</span>}
              </div>
              {c.work.length === 0 ? (
                <div className="text-xs text-muted">No open work linked to this client.</div>
              ) : (
                <div className="space-y-1">
                  {c.work.slice(0, 4).map((w) => (
                    <div key={w.id} className="flex items-baseline gap-2 text-sm">
                      <span className={w.overdue ? 'text-stall' : 'text-paper-100/85'}>{w.title}</span>
                      {w.due && (
                        <span className={`text-[11px] ml-auto shrink-0 ${w.overdue ? 'text-stall' : 'text-muted'}`}>
                          {w.overdue ? 'due ' : ''}{fmtDue(w.due)}
                        </span>
                      )}
                      {!w.linked && <span className="text-[9px] text-watch/70 shrink-0" title="matched by name, not FK-linked">~</span>}
                    </div>
                  ))}
                  {c.work.length > 4 && <div className="text-[11px] text-muted">+{c.work.length - 4} more</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* @block 2.4 Data-gap tiles (same honesty contract as Today) */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[2px] text-muted mb-1">Data gaps · being wired</div>
        <div className="rounded-lg border border-watch/25 bg-watch/5 px-3 py-2 text-xs">
          <span className="text-watch font-semibold">Delivery layer:</span>{' '}
          <span className="text-paper-100/70">hq_delivery (engagements / deliverables) is empty — cards are deal + task driven until it's populated.</span>
        </div>
        {totalUnlinked > 0 && (
          <div className="rounded-lg border border-watch/25 bg-watch/5 px-3 py-2 text-xs">
            <span className="text-watch font-semibold">Unlinked work ({totalUnlinked}):</span>{' '}
            <span className="text-paper-100/70">these items (marked ~) are matched to a client by name, not FK-linked. Durable fix: backfill task → deal links.</span>
          </div>
        )}
        <div className="rounded-lg border border-watch/25 bg-watch/5 px-3 py-2 text-xs">
          <span className="text-watch font-semibold">Last-touch:</span>{' '}
          <span className="text-paper-100/70">cold-client detection (deals.last_activity_at, now wired) isn't exposed on this endpoint yet — coming next.</span>
        </div>
      </div>
    </div>
  );
}
